
import { Button } from "@/components/ui/button";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { format } from "date-fns";
import { 
  ExternalLink, 
  LinkIcon, 
  Info, 
  ChevronDown, 
  ChevronUp 
} from "lucide-react";
import { ConversationStatusBadge } from "./ConversationStatusBadge";

interface Conversation {
  id: string;
  conversation_id?: string;
  status: string;
  creation_time?: string;
  call_duration_secs?: number;
  duration_seconds?: number;
  start_time_unix_secs?: number;
  to_number?: string;
  lead_id?: string;
  lead_name?: string;
}

interface ConversationsTableProps {
  conversations: Conversation[];
  isLoading: boolean;
  selectedConversation: string | null;
  isLinking: Record<string, boolean>;
  handleLinkToLead: (conversationId: string, phoneNumber: string) => void;
  handleViewDetails: (conversationId: string) => void;
  formatDate: (dateString?: string) => string;
  formatUnixDate: (unixSecs?: number) => string;
  truncateId: (id: string | undefined) => string;
}

export const ConversationsTable = ({
  conversations,
  isLoading,
  selectedConversation,
  isLinking,
  handleLinkToLead,
  handleViewDetails,
  formatDate,
  formatUnixDate,
  truncateId
}: ConversationsTableProps) => {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Status</TableHead>
            <TableHead>Started</TableHead>
            <TableHead>Duration</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Lead</TableHead>
            <TableHead>ID</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {conversations.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                {isLoading ? 'Loading conversations...' : 'No conversations found'}
              </TableCell>
            </TableRow>
          ) : (
            conversations.map((conversation) => (
              <TableRow key={conversation.id}>
                <TableCell><ConversationStatusBadge status={conversation.status || 'unknown'} /></TableCell>
                <TableCell>
                  {conversation.creation_time 
                    ? formatDate(conversation.creation_time) 
                    : conversation.start_time_unix_secs 
                      ? formatUnixDate(conversation.start_time_unix_secs)
                      : 'N/A'}
                </TableCell>
                <TableCell>
                  {conversation.call_duration_secs 
                    ? `${conversation.call_duration_secs}s` 
                    : conversation.duration_seconds 
                      ? `${conversation.duration_seconds}s`
                      : 'N/A'}
                </TableCell>
                <TableCell>
                  {conversation.to_number ? conversation.to_number : 'N/A'}
                </TableCell>
                <TableCell>
                  {conversation.lead_name ? (
                    conversation.lead_name
                  ) : (
                    conversation.to_number && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleLinkToLead(conversation.id, conversation.to_number!)}
                        disabled={isLinking[conversation.id]}
                        className="px-2 h-8"
                      >
                        <LinkIcon className="h-3.5 w-3.5 mr-1" />
                        Link to lead
                      </Button>
                    )
                  )}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {truncateId(conversation.conversation_id || conversation.id)}
                </TableCell>
                <TableCell>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="px-2 h-8"
                      onClick={() => handleViewDetails(conversation.conversation_id || conversation.id)}
                    >
                      <Info className="h-4 w-4 mr-1" />
                      {selectedConversation === conversation.id ? 
                        <ChevronUp className="h-3 w-3" /> : 
                        <ChevronDown className="h-3 w-3" />
                      }
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      asChild
                    >
                      <a href={`/admin/conversations/${conversation.conversation_id || conversation.id}`} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};
